import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaComments,
  FaHandsHelping,
  FaChartLine,
  FaHeart,
  FaGlobeAfrica,
} from "react-icons/fa";

const CommunitySection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);

  @media (max-width: 768px) {
    padding: 4rem 0;
  }

  @media (max-width: 480px) {
    padding: 3rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 1rem;

  @media (max-width: 1024px) {
    font-size: 2.5rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.2rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto 4rem auto;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const CommunityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const CommunityCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 93, 59, 0.15);
  }
`;

const CommunityIcon = styled.div`
  font-size: 3rem;
  color: #005d3b;
  margin-bottom: 1rem;
`;

const CommunityTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
`;

const CommunityDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const CommunityFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
`;

const CommunityFeature = styled.span`
  background: #f0f8f0;
  color: #005d3b;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const StatsSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-top: 4rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  text-align: center;
`;

const StatItem = styled(motion.div)`
  h3 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #005d3b;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-weight: 500;
  }
`;

const Community = () => {
  const communityAspects = [
    {
      icon: <FaUsers />,
      title: "Social Media Citizens",
      description:
        "Engage with Ugandans active on social media who care about politics, social issues, and community contribution.",
      features: [
        "18-35 years",
        "Politics & social issues",
        "Trending topics",
        "Opinion sharing",
      ],
    },
    {
      icon: <FaHandsHelping />,
      title: "Middle-Class Professionals",
      description:
        "Connect with educated, employed professionals who value accountability and efficient solutions.",
      features: [
        "25-45 years",
        "Educated & employed",
        "Fast internet access",
        "Values-driven",
      ],
    },
    {
      icon: <FaComments />,
      title: "Everyday Workers",
      description:
        "Support informal workers who seek relevance, fairness, and motivation for progress.",
      features: [
        "20-55 years",
        "Informal sector",
        "Simple solutions",
        "Progress-focused",
      ],
    },
    {
      icon: <FaChartLine />,
      title: "Impact Tracking",
      description:
        "Measure the real impact of community actions and track progress towards accountability goals.",
      features: [
        "Progress metrics",
        "Impact visualization",
        "Success stories",
        "Data insights",
      ],
    },
    {
      icon: <FaHeart />,
      title: "Ubuntu Spirit",
      description:
        "Embody the African philosophy of Ubuntu - connection, empathy, and belonging in all we do.",
      features: [
        "Human connection",
        "Empathy & compassion",
        "Community belonging",
        "Shared humanity",
      ],
    },
    {
      icon: <FaGlobeAfrica />,
      title: "African Identity",
      description:
        "Celebrate and strengthen African identity through authentic, locally-relevant digital solutions.",
      features: [
        "Cultural authenticity",
        "Local context",
        "African imagery",
        "Community pride",
      ],
    },
  ];

  const stats = [
    { number: "10K+", label: "Active Community Members" },
    { number: "500+", label: "Community Actions Taken" },
    { number: "25+", label: "Communities Impacted" },
    { number: "100%", label: "African-Focused" },
  ];

  return (
    <CommunitySection id="community">
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Our Community
        </SectionTitle>

        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Diverse but connected by care for community - where every voice counts
        </SectionSubtitle>

        <CommunityGrid>
          {communityAspects.map((aspect, index) => (
            <CommunityCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <CommunityIcon>{aspect.icon}</CommunityIcon>
              <CommunityTitle>{aspect.title}</CommunityTitle>
              <CommunityDescription>{aspect.description}</CommunityDescription>
              <CommunityFeatures>
                {aspect.features.map((feature, featureIndex) => (
                  <CommunityFeature key={featureIndex}>
                    {feature}
                  </CommunityFeature>
                ))}
              </CommunityFeatures>
            </CommunityCard>
          ))}
        </CommunityGrid>

        <StatsSection>
          <StatsGrid>
            {stats.map((stat, index) => (
              <StatItem
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3>{stat.number}</h3>
                <p>{stat.label}</p>
              </StatItem>
            ))}
          </StatsGrid>
        </StatsSection>
      </Container>
    </CommunitySection>
  );
};

export default Community;
